<template>
  <basic-modal.container
    id="productModal"
    ref="modalRef"
    @close="$emit('close')"
  >
    <basic-modal.step
      v-if="activeView === 'detail'"
      :id="`detail_${props.product.productId}`"
      size="maxi"
      type="progress"
      :step-title="modalTitleContent"
      :primary-label="actions.primary.label"
      :primary-visible="actions.primary.visible"
      :secondary-label="actions.secondary.label"
      :secondary-visible="actions.secondary.visible"
      :tertiary-label="actions.tertiary.label"
    >
      <basic-modal.content>
        <product-detail
          :id="$props.id"
          :category-id="$props.categoryId"
          :group-id="$props.groupId"
          :person="$props.person"
          :product="localProduct"
          :source="$props.source"
          :options="$props.options"
          :options-mode="isProductEditable ? 'write' : 'read'"
          @change="changeOptions"
          @close="event => $emit('close', event)"
          @switch-view="view => (activeView = view)"
        />
      </basic-modal.content>

      <basic-modal.footer
        @click-primary="actions.primary.action"
        @click-secondary="actions.secondary.action"
        @click-tertiary="actions.tertiary.action"
      />
    </basic-modal.step>

    <basic-modal.step
      v-else-if="activeView === 'benefits'"
      :id="`benefits_${props.product.productId}`"
      :step-title="modalTitleContent"
      :primary-label="$t('form.back')"
      size="maxi"
      type="acknowledgement"
    >
      <basic-modal.content>
        <product-benefits :product-id="$props.product.productId" />
      </basic-modal.content>

      <basic-modal.footer @click-primary="activeView = 'detail'" />
    </basic-modal.step>

    <basic-modal.step
      v-else-if="activeView === 'hospital'"
      :id="`hospitalSearch_${props.product.productId}`"
      :step-title="modalTitleContent"
      :primary-label="$t('form.back')"
      size="maxi"
      type="acknowledgement"
    >
      <basic-modal.content>
        <product-hospital-search :product-id="$props.product.productId" />
      </basic-modal.content>

      <basic-modal.footer @click-primary="activeView = 'detail'" />
    </basic-modal.step>
  </basic-modal.container>
</template>

<script setup>
  import { computed, ref, watch } from 'vue'
  import { cloneDeep } from 'lodash'

  import { events$, loading$ } from '@shared/services'
  import { ReactivityUtil } from '@shared/utils/Reactivity'
  import { isProductReadOnlyHaX } from '@shared/utils/HaX'

  import basketStore from '@shared/store/basket'
  import useI18n from '@shared/hooks/useI18n'
  import useProduct from '@shared/hooks/useProduct'
  import useProductPersons from '@shared/hooks/useProductPersons'
  import useProductState from '@shared/hooks/useProductState'
  import usePersonSpecificProduct from '@shared/hooks/usePersonSpecificProduct'

  import { BasicModal } from '@shared/components/Basic'
  import { ProductDetail, ProductBenefits, ProductHospitalSearch } from '@shared/components/Product/Modal'

  import { PRODUCT_STATE } from '@shared/config/constants'
  import { EVENT_BASKET, EVENT_MEDICAL_OFFICE_MODAL_OPEN, EVENT_PRODUCT } from '@shared/config/events'

  // HOOKS
  const { t } = useI18n()
  const { isUpgrade } = useProduct()
  const { addProductDoctorToBasket, addProductToOvpBasket, addProductWithDoctor } = useProductPersons()
  const { state } = useProductState()
  const { getSimulatedProductSelection, removeProductFromPerson, updateProductForPerson, willReplace } =
    usePersonSpecificProduct()

  // INIT
  defineEmits(['close'])
  const props = defineProps({
    categoryId: {
      type: String,
      required: true,
    },

    groupId: {
      type: String,
      required: true,
    },

    id: {
      type: String,
      required: true,
    },

    modalTitle: {
      type: String,
      default: null,
    },

    options: {
      type: Boolean,
      default: true,
    },

    person: {
      type: Object,
      required: true,
    },

    product: {
      type: Object,
      required: true,
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    source: {
      type: String,
      required: true,
    },
  })

  // DATA
  const modalRef = ref()
  const localProduct = ref(ReactivityUtil.clone(props.product))
  const isDirty = ref(false)
  const activeView = ref(null)

  // COMPUTED
  const actions = computed(() => {
    const data = {}

    data.primary = {
      action: async () => {
        // needs doctor? -> open medicalOfficeSearch
        if (localProduct.value.medicalOffice && !localProduct.value.doctor) {
          events$.emit(EVENT_MEDICAL_OFFICE_MODAL_OPEN, {
            source: props.source,
            personId: personId.value,
            product: localProduct.value,

            callback: async option => {
              loading$.start({ blocking: false })

              try {
                await changeOptions(option, false)
                await addProduct(false)

                loading$.end()
              } catch {
                loading$.failed()
              }
            },
          })

        // will replace another product? -> open replace modal
        } else if (willReplaceIds.value && willReplaceIds.value !== localProduct.value.productId) {
          events$.emit(EVENT_PRODUCT.REPLACE_OPEN, {
            fromProductId: willReplaceIds.value,
            person: props.person,
            toProduct: ReactivityUtil.clone(localProduct.value),
            source: props.source,
          })

        // product not yet selected? -> add product
        } else if (!localProduct.value.selected) {
          await addProduct()

        // product selected? -> apply changes
        } else {
          await applyChanges()
        }

        modalRef.value?.close()
      },
      label: localProduct.value.selected ? t('form.apply') : t('product.actions.add'),
      visible: !!props.options && (props.product.valid || localProduct.value.selected),
    }

    data.secondary = {
      action: async () => {
        modalRef.value.close()

        await removeProduct()
      },
      label: t('product.actions.remove'),
      visible: !!props.options && localProduct.value.selected && productState.value === PRODUCT_STATE.EDITABLE,
    }

    data.tertiary = {
      action: () => {
        events$.emit(EVENT_BASKET.CLEAR_AFTER_UPDATE)

        ReactivityUtil.reAssign(localProduct.value, props.product)
        modalRef.value.close()
      },
      label: t('form.cancel'),
    }

    return data
  })

  const isProductEditable = computed(() => {
    return productState.value !== PRODUCT_STATE.READONLY && !isProductReadOnlyHaX(productId.value)
  })

  const modalTitleContent = computed(() => {
    return props.modalTitle ? props.modalTitle : t('product.moreInformation')
  })

  const personId = computed(() => {
    return props.person.personId
  })

  const productId = computed(() => {
    return props.product.productId
  })

  const productState = computed(() => {
    const partnerNumber = props.person.partnerNumber
    return state(partnerNumber, productId.value)
  })

  const willReplaceIds = computed(() => {
    return willReplace(personId.value, productId.value)
  })

  // METHODS
  async function addProduct(showLoader = true) {
    if (showLoader) loading$.start({ blocking: false })

    try {
      // Product is CAREMED, do the dance around the "feeling lucky"-dialog
      if (productId.value === 'ACSProdukt') {
        await addProductWithDoctor({
          categoryId: props.categoryId,
          doctor: localProduct.value.doctor,
          personId: personId.value,
          productId: localProduct.value.productId,
          source: props.source,
        })

        if (showLoader) loading$.end()

      // any other product
      } else {
        const mode = isUpgrade(productId.value) ? 'ADD' : 'REPLACE'
        let basket = updateProductForPerson({ personId: personId.value, product: localProduct.value })
        basket = await addProductToOvpBasket(personId.value, productId.value, { basket, mode })
        basket = await basketStore.updateOvpBasket(basket)

        events$.emit(EVENT_PRODUCT.ADDED, {
          basket,
          categoryId: props.categoryId,
          personId: personId.value,
          productId: props.product.productId,
          source: props.source,
        })

        ReactivityUtil.reAssign(localProduct.value, props.product)

        isDirty.value = false
        if (showLoader) loading$.end()

        return Promise.resolve()
      }
    } catch {
      if (showLoader) loading$.failed()

      return Promise.reject()
    }
  }

  async function applyChanges() {
    if (!isDirty.value) return Promise.resolve()

    loading$.start({ blocking: false })

    try {
      let basket = updateProductForPerson({ personId: personId.value, product: localProduct.value })

      if (localProduct.value.doctor) {
        basket = await addProductDoctorToBasket({
          personId: personId.value,
          productId: productId.value,
          doctor: localProduct.value.doctor,
          basket,
        })
      }

      await basketStore.updateOvpBasket(basket)

      events$.emit(EVENT_PRODUCT.CHANGED, {
        basket,
        categoryId: props.categoryId,
        personId: personId.value,
        productId: props.product.productId,
        source: props.source,
      })

      isDirty.value = false

      loading$.end()

      return Promise.resolve()
    } catch (error) {
      console.error(error)
      loading$.failed()
      return Promise.reject()
    }
  }

  async function changeOptions(option, showLoader = true) {
    if (showLoader) loading$.start()

    try {
      const updatedProduct = await getSimulatedProductSelection({
        product: cloneDeep(localProduct.value),
        personId: personId.value,
        option,
      })

      ReactivityUtil.reAssign(localProduct.value, updatedProduct)
      if (localProduct.value.selected) {
        isDirty.value = true
      }

      if (showLoader) loading$.end()
      return Promise.resolve()
    } catch (error) {
      console.error(error)
      if (showLoader) loading$.failed()
      return Promise.reject()
    }
  }

  async function removeProduct() {
    loading$.start()

    try {
      const __basket = await removeProductFromPerson(personId.value, productId.value)

      events$.emit(EVENT_PRODUCT.REMOVED, {
        basket: __basket,
        categoryId: props.categoryId,
        personId: personId.value,
        productId: productId.value,
        source: props.source,
      })

      ReactivityUtil.reAssign(localProduct.value, props.product)

      isDirty.value = false
      loading$.end()

      return Promise.resolve()
    } catch {
      loading$.failed()
    }
  }

  // WATCHER
  watch(
    () => modalRef.value?.isModalActive,
    async isModalActive => {
      // @note: always open the modal in the detail view
      if (isModalActive) {
        activeView.value = 'detail'
      }

      if (isModalActive && !props.product.selected) {
        try {
          loading$.start()

          localProduct.value = Object.assign(
            {},
            props.product,
            await getSimulatedProductSelection({
              productId: productId.value,
              personId: personId.value,
            })
          )

          loading$.end()
        } catch {
          loading$.failed()
        }
      }
    }
  )

  watch(
    () => props.product,
    value => {
      ReactivityUtil.reAssign(localProduct.value, value)
    },
    { deep: true }
  )

  // EPILOGUE
  defineExpose({ modalRef })
</script>

<style scoped>
.box {
  display: flex;
  flex-direction: column;
  gap: var(--fixed-spacing-fix-04);

  .product__pricing {
    margin: calc(-1 * var(--fixed-spacing-fix-02)) 0 0 0;
  }

  .product__extra {
    display: flex;
    flex-direction: column;
    gap: var(--fixed-spacing-fix-02);

    &:not(:empty) {
      margin: var(--fixed-spacing-fix-02) 0 var(--fixed-spacing-fix-04) 0;
    }
  }

  .product__content {
    margin: calc(-1 * var(--fixed-spacing-fix-06)) 0 0 0;
  }

  .product__bullets {
    padding: 0 0 0 var(--fixed-spacing-fix-06);
  }

  .product__bullet .icon {
    margin: 0 0 0 4px;
  }

  .product__message:not(:empty) {
    display: flex;
    flex-direction: column;
    gap: var(--fixed-spacing-fix-04);
    margin: var(--fixed-spacing-fix-02) 0;
  }

  & > div:empty {
    display: none;
  }
}

@media (--v-medium) {
  .box {
    .product__extra {
      flex-direction: row;
      gap: var(--fixed-spacing-fix-08);
    }
  }
}
</style>
